/**
 * End-of-summer sale landing page behaviour.
 *
 * Independent of ic-landing.js by design. The two pages share a stylesheet but not
 * a script: ic-landing.js is under active edit and binds to `[data-icl]`, so this
 * file binds to `[data-ics]` and can never be broken by a change over there.
 *
 * The Shopify integration is the same contract ic-landing.js established, and it is
 * the part that is easy to get wrong on this theme:
 *  - add via `cart/add.js`, re-fetch `cart.js`, then emit the theme's `cartUpdate`
 *    event, which is what keeps the drawer, the header count and the free-gift
 *    module in sync
 *  - `MinimogTheme.pubSubEvents.openCartDrawer` is a NO-OP here; call drawer.open()
 *  - the threshold meter EXCLUDES lines carrying `_free_gift`, matching
 *    snippets/cart-free-gift.liquid, so the gift can never pay for itself
 *
 * The sale-specific job is the upsell sequence: the hero add is the only moment the
 * shopper has committed to anything, so that is when the ladder gets surfaced.
 */
(function () {
  var root = document.querySelector('[data-ics]');
  if (!root || root.dataset.icsReady === 'true') return;
  root.dataset.icsReady = 'true';

  var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var threshold = parseInt(root.dataset.threshold, 10);
  if (isNaN(threshold)) threshold = 7500;
  var moneyFormat = root.dataset.moneyFormat || '${{amount}}';
  var heroHandle = root.dataset.heroHandle || '';

  var heroBox = root.querySelector('[data-ics-hero]');
  var grid = root.querySelector('[data-ics-grid]');
  var dock = root.querySelector('[data-ics-dock]');
  var peek = root.querySelector('[data-ics-dock-open]');
  var peekLabel = root.querySelector('[data-ics-peek-label]');
  var nudge = root.querySelector('[data-ics-nudge]');

  /* ---------- money ---------- */

  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withCommas = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    var amount = withCommas + '.' + parts[1];
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withCommas);
  }

  function parseJSONAttr(el, name, fallback) {
    if (!el) return fallback;
    try { return JSON.parse(el.dataset[name] || ''); } catch (e) { return fallback; }
  }

  /* ---------- variant resolution ---------- */

  // Metal is chosen once, in the hero, and then honoured everywhere else on the
  // page. Offering a shopper a metal they did not pick is how the attach is lost.
  function heroMetal() {
    if (!heroBox) return '';
    var pressed = heroBox.querySelector('[data-ics-metal][aria-pressed="true"]');
    return pressed ? pressed.dataset.metal : (heroBox.dataset.defaultMetal || '');
  }

  function pickVariant(variants, metal) {
    if (!variants || !variants.length) return null;
    if (!metal) return variants.find(function (v) { return v.available; }) || variants[0];
    var match = variants.filter(function (v) {
      return String(v.metal).toLowerCase() === String(metal).toLowerCase();
    });
    // Fall back to any purchasable variant rather than refusing the add: a silver
    // shopper looking at a gold-only piece should still be able to buy it.
    return match.find(function (v) { return v.available; }) ||
           match[0] ||
           variants.find(function (v) { return v.available; }) ||
           null;
  }

  function cardMetal(card) {
    var pressed = card.querySelector('[data-ics-metal][aria-pressed="true"]');
    return pressed ? pressed.dataset.metal : (card.dataset.defaultMetal || '');
  }

  /* ---------- cart state ---------- */

  var inCart = {};

  function eligibleSubtotal(cart) {
    return (cart.items || []).reduce(function (sum, item) {
      var props = item.properties || {};
      if (props._free_gift) return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  function paint(cart) {
    var subtotal = eligibleSubtotal(cart);
    var count = cart.item_count || 0;

    inCart = {};
    (cart.items || []).forEach(function (item) {
      if (item.handle) inCart[item.handle] = true;
    });

    if (dock) {
      var earned = subtotal >= threshold;
      dock.classList.toggle('is-earned', earned);
      dock.classList.toggle('has-items', count > 0);

      var msg = root.querySelector('[data-ics-meter-msg]');
      if (msg) {
        msg.innerHTML = earned
          ? (root.dataset.msgEarned || 'Free shipping and your free charm are <b>unlocked</b>')
          : (root.dataset.msgProgress || 'Add <b>%amount%</b> for free shipping and a free charm')
              .replace('%amount%', formatMoney(threshold - subtotal));
      }
      var amt = root.querySelector('[data-ics-meter-amt]');
      if (amt) amt.textContent = formatMoney(subtotal);
      var fill = root.querySelector('[data-ics-fill]');
      if (fill) fill.style.width = Math.min(100, threshold ? (subtotal / threshold) * 100 : 0) + '%';
      var itemsEl = root.querySelector('[data-ics-items]');
      if (itemsEl) itemsEl.textContent = count + (count === 1 ? ' item' : ' items');
      var totalEl = root.querySelector('[data-ics-total]');
      if (totalEl) totalEl.textContent = formatMoney(cart.total_price || 0);
      if (peekLabel) peekLabel.textContent = count + ' · ' + formatMoney(cart.total_price || 0);
      syncDock();
    }

    // Hero button reflects membership too — a shopper who scrolls back up needs to
    // see the piece is already in the bag rather than adding a second one.
    var heroBtn = root.querySelector('[data-ics-hero-add]');
    if (heroBtn && heroHandle) {
      var heroIn = !!inCart[heroHandle];
      heroBtn.classList.toggle('is-in', heroIn);
      if (heroIn) heroBtn.textContent = root.dataset.labelIn || 'In bag';
    }

    if (grid) {
      grid.querySelectorAll('[data-ics-add]').forEach(function (btn) {
        var card = btn.closest('[data-ics-card]');
        if (!card) return;
        var isIn = !!inCart[card.dataset.handle];
        btn.classList.toggle('is-in', isIn);
        btn.textContent = isIn ? (root.dataset.labelIn || 'In bag') : (root.dataset.labelAdd || 'Add');
      });
    }

    // A tier whose pieces are all in the bag has nothing left to sell.
    root.querySelectorAll('[data-ics-tier]').forEach(function (tier) {
      var handles = (tier.dataset.handles || '').split(',').filter(Boolean);
      var complete = handles.length > 0 && handles.every(function (h) { return inCart[h]; });
      tier.classList.toggle('is-complete', complete);
      var btn = tier.querySelector('[data-ics-tier-add]');
      if (btn && complete && !btn.disabled) btn.textContent = root.dataset.labelIn || 'In bag';
    });
  }

  function refresh() {
    return fetch(rootUrl + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { paint(cart); return cart; });
  }

  function showError(error) {
    var note = root.querySelector('[data-ics-error]');
    if (!note) return;
    note.textContent = (error && error.message) || 'Something went wrong. Please try again.';
    note.hidden = false;
    window.setTimeout(function () { note.hidden = true; }, 6000);
  }

  function addItems(items, button) {
    if (button) button.classList.add('is-busy');
    return fetch(rootUrl + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');
        return refresh();
      })
      .then(function (cart) {
        if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
          MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, cart);
        }
        var drawer = document.querySelector('m-cart-drawer');
        if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
          drawer.onCartDrawerUpdate();
          drawer.classList.toggle('m-cart--empty', cart.item_count === 0);
        }
        return cart;
      })
      .catch(function (error) { showError(error); })
      .finally(function () { if (button) button.classList.remove('is-busy'); });
  }

  /* ---------- hero gallery ---------- */

  var galImg = root.querySelector('[data-ics-gal-img]');

  function setGalleryImage(url, alt, w, h) {
    if (!galImg || !url) return;
    // image_tag emitted a srcset; with one present the browser keeps resolving from
    // it and the photo never visibly changes, so it has to be rebuilt or dropped.
    if (/([?&])width=1000(&|$)/.test(url)) {
      galImg.setAttribute('srcset', [500, 750, 1000].map(function (n) {
        return url.replace(/([?&])width=1000(&|$)/, '$1width=' + n + '$2') + ' ' + n + 'w';
      }).join(', '));
    } else {
      galImg.removeAttribute('srcset');
    }
    galImg.setAttribute('src', url);
    if (alt != null) galImg.setAttribute('alt', alt);
    if (w) galImg.setAttribute('width', w);
    if (h) galImg.setAttribute('height', h);
  }

  root.querySelectorAll('[data-ics-thumb]').forEach(function (thumb) {
    thumb.addEventListener('click', function () {
      root.querySelectorAll('[data-ics-thumb]').forEach(function (t) {
        t.classList.remove('is-on');
        t.setAttribute('aria-pressed', 'false');
      });
      thumb.classList.add('is-on');
      thumb.setAttribute('aria-pressed', 'true');
      setGalleryImage(thumb.dataset.full, thumb.dataset.alt, thumb.dataset.w, thumb.dataset.h);
    });
  });

  /* ---------- hero metal + add ---------- */

  // `fromPicker` is the difference between the shopper choosing a metal and the page
  // merely booting. On boot the hero is a deliberately chosen campaign frame, and the
  // gold variant also owns a white flatlay, so syncing the image unconditionally
  // replaced the editorial shot with a packshot the moment the page loaded. Only an
  // explicit metal choice earns the swap.
  function syncHeroPrice(fromPicker) {
    if (!heroBox) return;
    var variants = parseJSONAttr(heroBox, 'variants', []);
    var v = pickVariant(variants, heroMetal());
    if (!v) return;

    var nowEl = root.querySelector('[data-ics-now]');
    var wasEl = root.querySelector('[data-ics-was]');
    var saveEl = root.querySelector('[data-ics-save]');
    if (nowEl) nowEl.textContent = formatMoney(v.price);

    // Only claim a saving when this specific variant really carries one. A metal
    // that was not marked down must not inherit the other metal's strike-through.
    var hasSale = v.compare_at && v.compare_at > v.price;
    if (wasEl) {
      wasEl.hidden = !hasSale;
      if (hasSale) wasEl.textContent = formatMoney(v.compare_at);
    }
    if (saveEl) {
      saveEl.hidden = !hasSale;
      if (hasSale) saveEl.textContent = 'Save ' + formatMoney(v.compare_at - v.price);
    }

    var nameEl = root.querySelector('[data-ics-metal-name]');
    if (nameEl && v.metal) nameEl.textContent = v.metal;

    var btn = root.querySelector('[data-ics-hero-add]');
    if (btn && !inCart[heroHandle]) {
      btn.disabled = !v.available;
      btn.textContent = v.available ? (root.dataset.labelAddToBag || btn.dataset.label || btn.textContent) : 'Sold out';
    }

    if (fromPicker && v.image) {
      setGalleryImage(v.image, heroBox.dataset.title);
      // Keep the thumbnail row honest about what is on screen.
      root.querySelectorAll('[data-ics-thumb]').forEach(function (t) {
        var match = t.dataset.full && v.image &&
                    t.dataset.full.split('?')[0] === v.image.split('?')[0];
        t.classList.toggle('is-on', !!match);
        t.setAttribute('aria-pressed', match ? 'true' : 'false');
      });
    }
  }

  if (heroBox) {
    heroBox.addEventListener('click', function (event) {
      var metalBtn = event.target.closest('[data-ics-metal]');
      if (metalBtn) {
        if (metalBtn.disabled) return;
        heroBox.querySelectorAll('[data-ics-metal]').forEach(function (b) {
          b.setAttribute('aria-pressed', 'false');
        });
        metalBtn.setAttribute('aria-pressed', 'true');
        syncHeroPrice(true);
        // The chosen metal now drives every tier on the page, so their prices and
        // availability have to be recomputed too.
        paintTiers();
        return;
      }

      var addBtn = event.target.closest('[data-ics-hero-add]');
      if (addBtn) {
        var variants = parseJSONAttr(heroBox, 'variants', []);
        var v = pickVariant(variants, heroMetal());
        if (!v || !v.available) return;
        addItems([{ id: v.id, quantity: 1, properties: { _ics: 'sale' } }], addBtn)
          .then(function () { openNudge(); });
      }
    });
  }

  /* ---------- the upsell moment ---------- */

  // Fired only after the hero is in the bag. Points at the cheapest tier the
  // shopper has not already completed, because the smallest next step is the one
  // most likely to be taken.
  function openNudge() {
    if (!nudge) return;
    var tiers = Array.prototype.slice.call(root.querySelectorAll('[data-ics-tier]'));
    var next = tiers
      .filter(function (t) {
        var handles = (t.dataset.handles || '').split(',').filter(Boolean);
        return !handles.every(function (h) { return inCart[h]; });
      })
      .sort(function (a, b) {
        return (parseInt(a.dataset.save, 10) || 0) - (parseInt(b.dataset.save, 10) || 0);
      })[0];

    if (!next) { nudge.hidden = true; return; }

    var name = next.querySelector('.ics-tierName');
    // The tier is picked here; the copy above it is fixed. Prices were removed
    // from this block deliberately: the shopper has just seen the price on the
    // button they pressed, and repeating a second number here made the panel
    // something to read rather than something to glance at.

    // Show the pieces. Seeing the stack is most of the argument, and the shots are
    // already rendered on the tier, so they are cloned rather than fetched again.
    var shots = root.querySelector('[data-ics-nudge-shots]');
    if (shots) {
      shots.innerHTML = '';
      Array.prototype.slice.call(next.querySelectorAll('.ics-tierShot'), 0, 3)
        .forEach(function (img) {
          var c = document.createElement('img');
          c.src = img.currentSrc || img.src;
          c.alt = '';
          c.loading = 'lazy';
          shots.appendChild(c);
        });
    }

    nudge.hidden = false;
  }

  /* ---------- tiers ---------- */

  function paintTiers() {
    var metal = heroMetal();
    root.querySelectorAll('[data-ics-tier]').forEach(function (tier) {
      var products = parseJSONAttr(tier, 'products', []);
      var anyMissing = products.some(function (p) {
        var v = pickVariant(p.variants, metal);
        return !v || !v.available;
      });
      var btn = tier.querySelector('[data-ics-tier-add]');
      if (btn && !tier.classList.contains('is-complete')) {
        btn.disabled = anyMissing;
        if (anyMissing) btn.textContent = 'Sold out';
      }
    });
  }

  root.addEventListener('click', function (event) {
    var tierBtn = event.target.closest('[data-ics-tier-add]');
    if (!tierBtn || tierBtn.disabled) return;
    var tier = tierBtn.closest('[data-ics-tier]');
    if (!tier) return;

    // Re-read the cart before deciding what to add. `inCart` is a cache, and the
    // shopper can empty a line in the drawer without this page ever hearing about
    // it; trusting the cache there would skip a piece and quietly add a partial
    // set, which is worse than a slow button because the look discount needs EVERY
    // piece present to fire at all.
    tierBtn.classList.add('is-busy');
    refresh()
      .then(function () {
        var metal = heroMetal();
        var products = parseJSONAttr(tier, 'products', []);
        var items = [];
        for (var i = 0; i < products.length; i++) {
          var v = pickVariant(products[i].variants, metal);
          if (!v || !v.available) {
            throw new Error(products[i].title + ' is not available in ' + metal);
          }
          // Skip only what is genuinely in the bag right now, so tapping a tier
          // never doubles a line the shopper already added by hand.
          if (inCart[products[i].handle]) continue;
          items.push({ id: v.id, quantity: 1, properties: { _ics: 'sale-bundle' } });
        }
        if (!items.length) return null;
        return addItems(items, null);
      })
      .catch(showError)
      .finally(function () { tierBtn.classList.remove('is-busy'); });
  });

  /* ---------- grid cards ---------- */

  if (grid) {
    grid.addEventListener('click', function (event) {
      var metalBtn = event.target.closest('[data-ics-metal]');
      if (metalBtn) {
        if (metalBtn.disabled) return;
        var mCard = metalBtn.closest('[data-ics-card]');
        if (!mCard) return;
        mCard.querySelectorAll('[data-ics-metal]').forEach(function (b) {
          b.setAttribute('aria-pressed', 'false');
        });
        metalBtn.setAttribute('aria-pressed', 'true');
        showCardImage(mCard);
        return;
      }

      var addBtn = event.target.closest('[data-ics-add]');
      if (addBtn) {
        var card = addBtn.closest('[data-ics-card]');
        if (!card) return;
        var v = pickVariant(parseJSONAttr(card, 'variants', []), cardMetal(card));
        if (!v || !v.available) return;
        addItems([{ id: v.id, quantity: 1, properties: { _ics: 'sale-grid' } }], addBtn);
      }
    });
  }

  function showCardImage(card) {
    var img = card.querySelector('.icl-shot');
    if (!img) return;
    if (img.dataset.icsBaseSrc === undefined) {
      img.dataset.icsBaseSrc = img.getAttribute('src') || '';
      img.dataset.icsBaseSrcset = img.getAttribute('srcset') || '';
    }
    var v = pickVariant(parseJSONAttr(card, 'variants', []), cardMetal(card));
    var url = v && v.image;
    if (!url) {
      if (img.dataset.icsBaseSrc) img.setAttribute('src', img.dataset.icsBaseSrc);
      if (img.dataset.icsBaseSrcset) img.setAttribute('srcset', img.dataset.icsBaseSrcset);
      else img.removeAttribute('srcset');
      return;
    }
    if (/([?&])width=700(&|$)/.test(url)) {
      img.setAttribute('srcset', [300, 500, 700].map(function (w) {
        return url.replace(/([?&])width=700(&|$)/, '$1width=' + w + '$2') + ' ' + w + 'w';
      }).join(', '));
    } else {
      img.removeAttribute('srcset');
    }
    img.setAttribute('src', url);
  }

  if (grid) grid.querySelectorAll('[data-ics-card]').forEach(showCardImage);

  /* ---------- dock ----------
     The visibility contract lives in ic-landing.css, which this page shares, and it
     changed on 2026-08-19: the dock is hidden by the `hidden` ATTRIBUTE now, not by
     an `.is-hidden` class, and the stylesheet releases the theme's scroll-to-top
     button off `html.icl-dock-open`. Keep this in step with ic-landing.js or the
     bar silently stops hiding and starts fighting the theme's own fixed furniture. */

  // Its own storage key: dismissing the bar on the sale page should not also dismiss
  // it on the ad landing page, and vice versa.
  var DOCK_KEY = 'icsDockDismissed';

  function dockDismissed() {
    try { return sessionStorage.getItem(DOCK_KEY) === '1'; } catch (e) { return false; }
  }

  function rememberDock(dismissed) {
    try { sessionStorage.setItem(DOCK_KEY, dismissed ? '1' : '0'); } catch (e) {}
  }

  function syncDock() {
    if (!dock) return;
    var hidden = dockDismissed();
    dock.hidden = hidden;
    // Only offer the way back when there is a cart to get back to.
    if (peek) peek.hidden = !(hidden && dock.classList.contains('has-items'));
    document.documentElement.classList.toggle('icl-dock-open', !hidden);
    positionDock();
  }

  // Minimog renders `.m-mobile-sticky-bar` fixed to the bottom on small screens and
  // hides it further up. Measuring it beats hard-coding a breakpoint, which would
  // drift if the theme's md value ever changes.
  function positionDock() {
    if (!dock) return;
    var bar = document.querySelector('.m-mobile-sticky-bar');
    var barHeight = 0;
    if (bar) {
      var cs = getComputedStyle(bar);
      if (cs.display !== 'none' && cs.visibility !== 'hidden' && cs.position === 'fixed') {
        barHeight = bar.getBoundingClientRect().height;
      }
    }

    if (barHeight > 0) dock.style.bottom = barHeight + 'px';
    else dock.style.removeProperty('bottom'); // let the stylesheet's desktop rule win

    var peekHeight = 0;
    if (peek && !peek.hidden) {
      peek.style.bottom = (barHeight + 16) + 'px';
      peekHeight = peek.getBoundingClientRect().height + 16;
    }

    // Reserve exactly enough room that the closer is never trapped behind either bar.
    var dockHeight = dock.hidden ? 0 : dock.getBoundingClientRect().height;
    root.style.paddingBottom = Math.ceil(Math.max(dockHeight, peekHeight) + barHeight + 28) + 'px';

    // Gorgias parks its launcher bottom-right, the same corner the dock takes on
    // desktop. Below 900px the dock is full width and the stylesheet handles it.
    var chat = document.getElementById('chat-button');
    if (chat) {
      if (window.matchMedia('(min-width: 900px)').matches) {
        chat.style.setProperty('bottom', (dock.hidden ? 20 : Math.ceil(dockHeight + 36)) + 'px', 'important');
      } else {
        chat.style.removeProperty('bottom');
      }
    }
  }

  var closeBtn = root.querySelector('[data-ics-dock-close]');
  if (closeBtn) closeBtn.addEventListener('click', function () { rememberDock(true); syncDock(); });
  if (peek) peek.addEventListener('click', function () { rememberDock(false); syncDock(); });

  window.addEventListener('resize', positionDock);

  var checkout = root.querySelector('[data-ics-checkout]');
  if (checkout) {
    checkout.addEventListener('click', function () {
      // Shopify emits content_for_additional_checkout_buttons once per page and the
      // cart drawer already claims it, so the dock opens the drawer (which carries
      // the wallets) rather than posting straight to /checkout.
      var drawer = document.querySelector('m-cart-drawer');
      if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
      window.location.href = rootUrl + 'checkout';
    });
  }

  /* ---------- countdown ---------- */

  var bar = root.querySelector('[data-ics-bar]');
  if (bar && bar.dataset.ends) {
    var endsAt = Date.parse(bar.dataset.ends);
    var timer = root.querySelector('[data-ics-timer]');
    if (!isNaN(endsAt) && timer) {
      var tick = function () {
        var left = endsAt - Date.now();
        if (left <= 0) {
          // Never show a negative or rolled-over clock: if the sale is over the bar
          // stops claiming it is running.
          timer.textContent = 'Sale has ended';
          window.clearInterval(handle);
          return;
        }
        var d = Math.floor(left / 86400000);
        var h = Math.floor(left / 3600000) % 24;
        var m = Math.floor(left / 60000) % 60;
        var s = Math.floor(left / 1000) % 60;
        timer.innerHTML = 'Ends in ' +
          (d > 0 ? '<span class="ics-barUnit">' + d + 'd</span> ' : '') +
          '<span class="ics-barUnit">' + h + 'h</span> ' +
          '<span class="ics-barUnit">' + m + 'm</span>' +
          (d === 0 ? ' <span class="ics-barUnit">' + s + 's</span>' : '');
      };
      tick();
      var handle = window.setInterval(tick, 1000);
    }
  }

  /* ---------- boot ---------- */

  // The cart drawer, the free-gift module and the header count all mutate the cart
  // without going through this page. Listening to the theme's own event is what
  // keeps the dock meter, the In-bag labels and the tier state honest afterwards.
  if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, function (cart) {
      // Some emitters pass the cart, some pass nothing. Re-fetch when it is absent
      // rather than painting from undefined.
      if (cart && typeof cart.item_count === 'number') { paint(cart); paintTiers(); }
      else refresh().then(paintTiers);
    });
  }

  syncHeroPrice(false);
  refresh().then(paintTiers);
})();
